"use client";

import { useEffect, useState } from "react";

// Fires once the browser is idle OR the user first interacts — whichever comes
// first. Used to defer non-critical third-party scripts (analytics) off the
// initial main thread. The idle path has a safety timeout so visitors who never
// interact and bounce quickly are still counted shortly after load.
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
] as const;

export function useDeferredReady(timeout = 4000): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, trigger));
    };

    const trigger = () => {
      cleanup();
      setReady(true);
    };

    if ("requestIdleCallback" in window) {
      idleHandle = window.requestIdleCallback(trigger, { timeout });
    } else {
      timeoutHandle = setTimeout(trigger, timeout);
    }

    INTERACTION_EVENTS.forEach((e) =>
      window.addEventListener(e, trigger, { once: true, passive: true })
    );

    return cleanup;
  }, [timeout]);

  return ready;
}
